import { useState, useCallback, useEffect } from "react";
import {
  getOnboardingState,
  saveOnboardingState,
  getDefaultOnboardingState,
  type OnboardingState,
} from "@/infrastructure/storage";

export function useOnboarding() {
  const [state, setState] = useState<OnboardingState>(getDefaultOnboardingState());
  const [isInitialized, setIsInitialized] = useState(false);

  // Load state on mount
  useEffect(() => {
    setState(getOnboardingState());
    setIsInitialized(true);
  }, []);

  const markWelcomeSeen = useCallback(() => {
    setState((prev) => {
      const newState = { ...prev, hasSeenWelcome: true };
      saveOnboardingState(newState);
      return newState;
    });
  }, []);

  const markFirstGroupCreated = useCallback(() => {
    setState((prev) => {
      const newState = { ...prev, hasCreatedFirstGroup: true };
      saveOnboardingState(newState);
      return newState;
    });
  }, []);

  const markFirstExpenseAdded = useCallback(() => {
    setState((prev) => {
      const newState = { ...prev, hasAddedFirstExpense: true };
      saveOnboardingState(newState);
      return newState;
    });
  }, []);

  const markMemberInvited = useCallback(() => {
    setState((prev) => {
      const newState = { ...prev, hasInvitedMember: true };
      saveOnboardingState(newState);
      return newState;
    });
  }, []);

  const resetOnboarding = useCallback(() => {
    const defaultState = getDefaultOnboardingState();
    setState(defaultState);
    saveOnboardingState(defaultState);
  }, []);

  // Determine what step the user is at
  const currentStep = !state.hasSeenWelcome
    ? "welcome"
    : !state.hasCreatedFirstGroup
      ? "create-group"
      : !state.hasInvitedMember
        ? "invite-member"
        : !state.hasAddedFirstExpense
          ? "add-expense"
          : "complete";

  const isNewUser = !state.hasSeenWelcome;

  return {
    ...state,
    isInitialized,
    currentStep,
    isNewUser,
    markWelcomeSeen,
    markFirstGroupCreated,
    markFirstExpenseAdded,
    markMemberInvited,
    resetOnboarding,
  };
}
